<template>
  <div class="content-packed-circles-investment">

    <!-- chart here -->
    <div class="content-packed-circles-investment__chart">
      <!-- chart header -->
      <div v-if="content.chart" class="row">
        <div class="col-xs-12 col-12 col-sm-10 col-sm-offset-1 offset-sm-1 col-md-8 col-md-offset-2 offset-md-2">
          <h3>{{ content.chart.title }}</h3>
          <p class="hidden-sm hidden-md hidden-lg">{{ content.chart.subtitle }}</p>
        </div>
      </div>

      <!-- chart control -->
      <div class="impact-header__controls row" style="padding-top: 0">
        <div class="col-xs-12 col-12 col-sm-10 col-sm-offset-1 offset-sm-1 col-md-8 col-md-offset-2 offset-md-2">
          <packed-circle-controls
            class="mt-15"
            :category-parents="categoryParents"
            :category-parent.sync="categoryParent"
            :categories="categories"
            :category.sync="category"
            @show-percentage="updateShowPercentage"
          />
        </div>
      </div>

      <!-- chart here -->
      <div class="row" style="padding-top: 0">
        <div class="col-xs-12 col-12 col-sm-10 col-sm-offset-1 offset-sm-1 col-md-8 col-md-offset-2 offset-md-2">
          <template v-if="!$breakpoint.mobile">
            <packed-circles-viz
              class="mt-15"
              v-model="data"
              el-id="packed-circles-viz"
              :show-percentage="showPercentage"
            />
          </template>
          <!-- Mobile version -->
          <template v-else>
            <packed-circles-viz
              class="mt-15"
              v-model="dataMYes"
              :ratio="ratioMYes"
              el-id="packed-circles-m-yes-viz"
              :show-percentage="showPercentage"
            />
            <packed-circles-viz
              v-model="dataMNo"
              :input-ratio="ratioMNo"
              el-id="packed-circles-m-no-viz"
              :show-percentage="showPercentage"
            />
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as d3 from 'd3';
import PackedCircleControls from './charts/PackedCirclesControls.vue';
import PackedCirclesViz from './charts/PackedCirclesViz.vue';

export default {
  name: 'PackedCirclesInvestment',
  components: {
    PackedCircleControls,
    PackedCirclesViz,
  },
  props: {
    content: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      loadedData: [],
      categoryParent: 'Country',
      category: 'ASEAN-6',
      showPercentage: false,
      categoryParents: ['Country', 'Gender', 'Generation'],
      categories: ['ASEAN-6', 'Indonesia', 'Malaysia', 'Philippines', 'Singapore', 'Thailand', 'Vietnam'],
      data: null,
      dataMYes: null,
      dataMNo: null,
      rawData: null,
      rawDataTotal: null,
      rawDataMYes: null,
      rawDataMNo: null,
      rawDataCountry: null,
      rawDataCountryMYes: null,
      rawDataGender: null,
      rawDataGenderMYes: null,
      rawDataGeneration: null,
      rawDataGenerationMYes: null,
      ratioMYes: 1,
      ratioMNo: 1,
      dataSrc: `${process.env.BASE_URL}data/D1_packed.csv`,
      dataSrcTotal: `${process.env.BASE_URL}data/D1_packed_total.csv`,
    };
  },
  watch: {
    categoryParent(v) {
      this.updateCategoryParent(v);
    },
    category(v) {
      this.updateCategory(v);
    },
  },
  computed: {},
  methods: {
    loadData(data) {
      // get unique objects by "type" prop
      this.loadedData = [...new Map(data.map(({ type, commentary }) => [type, { type, commentary }])).values()];
    },
    updateShowPercentage(v) {
      this.showPercentage = v;
    },
    updateCategoryParent(v) {
      if (v === 'Country') {
        const rawDataCat = this.$breakpoint.mobile ? this.rawDataCountryMYes : this.rawDataCountry;
        this.categories = this.objectsArrayUnique(rawDataCat, 'Category');
        this.category = 'ASEAN-6';
      } else if (v === 'Gender') {
        const rawDataCat = this.$breakpoint.mobile ? this.rawDataGenderMYes : this.rawDataGender;
        this.categories = this.objectsArrayUnique(rawDataCat, 'Category');
        // Sort by Male first
        this.categories = this.categories.sort((a, b) => {
          const male = 1;
          const female = 2;

          let aWeight = 0;
          let bWeight = 0;

          if (a === 'Males') aWeight = male;
          else aWeight = female;

          if (b === 'Males') bWeight = male;
          else bWeight = female;

          return aWeight - bWeight;
        });
        this.category = 'Males';
      } else {
        // Generation
        const rawDataCat = this.$breakpoint.mobile ? this.rawDataGenerationMYes : this.rawDataGeneration;
        this.categories = this.objectsArrayUnique(rawDataCat, 'Category');
        // Sort by ages (Youth first)
        this.categories = this.categories.sort((a, b) => {
          const youth = 1;
          const millenials = 2;
          const genX = 3;
          const seniors = 4;

          let aWeight = 0;
          let bWeight = 0;

          if (a === 'Youths') aWeight = youth;
          else if (a === 'Gen Xers') aWeight = genX;
          else if (a === 'Millennials') aWeight = millenials;
          else aWeight = seniors;

          if (b === 'Youths') bWeight = youth;
          else if (b === 'Gen Xers') bWeight = genX;
          else if (b === 'Millennials') bWeight = millenials;
          else bWeight = seniors;

          return aWeight - bWeight;
        });
        this.category = 'Youths';
      }
    },
    updateCategory(v) {
      let rawData = null;
      let rawDataMYes = null;
      let rawDataMNo = null;
      if (this.categoryParent === 'Country') {
        if (!this.$breakpoint.mobile) rawData = this.rawDataCountry;
        else {
          rawDataMYes = this.rawDataCountryMYes;
          rawDataMNo = this.rawDataCountryMNo;
        }
      } else if (this.categoryParent === 'Gender') {
        if (!this.$breakpoint.mobile) rawData = this.rawDataGender;
        else {
          rawDataMYes = this.rawDataGenderMYes;
          rawDataMNo = this.rawDataGenderMNo;
        }
      } else {
        // Generation
        // eslint-disable-next-line no-lonely-if
        if (!this.$breakpoint.mobile) rawData = this.rawDataGeneration;
        else {
          rawDataMYes = this.rawDataGenerationMYes;
          rawDataMNo = this.rawDataGenerationMNo;
        }
      }
      if (rawData) this.data = this.formatData(rawData, 'Type', 'Percentage', v);
      if (rawDataMYes) {
        this.dataMYes = this.formatData(rawDataMYes, 'Type', 'Percentage', v, 'Yes');
      }
      if (rawDataMNo) {
        this.dataMNo = this.formatData(rawDataMNo, 'Type', 'Percentage', v, 'No');
      }
      if (rawDataMYes && rawDataMNo) {
        this.ratioMYes = Math.min((this.dataMYes.value / this.dataMNo.value) * 1.3, 1);
        this.ratioMNo = Math.min((this.dataMNo.value / this.dataMYes.value) * 1.3, 1);
      }
    },
    /**
     * Filters an array of objects by unique key and converts into 1 level array.
     * @param {Array} data
     * @param {string} key
     * @param {boolean} sort
     * @param {string} orderBy asc | desc
     */
    objectsArrayUnique(data, key, sort = false, orderBy = 'asc') {
      const flags = [];
      let output = [];

      data.forEach((d) => {
        if (flags[d[key]]) return;
        flags[d[key]] = true;
        output.push(d[key]);
      });

      if (sort) {
        output = output.sort();
        if (orderBy === 'desc') {
          output = output.reverse();
        }
      }

      return output;
    },
    formatData(rawData, nameColumn, valueColumn, category, mobileYesNo = null) {
      const grouppedYesNo = d3.group(
        rawData,
        (d) => d['Yes/No'],
        (d) => d.Category
      );

      // Calculate total percentage for both yes and no data
      const yesTotalPercentage =
        this.rawDataTotal.filter((d) => d['Yes/No'] === 'Yes' && d.Category === category)[0]?.Percentage || 0;
      const noTotalPercentage =
        this.rawDataTotal.filter((d) => d['Yes/No'] === 'No' && d.Category === category)[0]?.Percentage || 0;
      // Manipulate suitable ratio for the data (make sure it's not really big difference between both data)
      const ratioTotalPercentage =
        Math.max(yesTotalPercentage, noTotalPercentage) / Math.min(yesTotalPercentage, noTotalPercentage);
      let newRatioTotalPercentage = 1;
      const minTotalPercentage = yesTotalPercentage < noTotalPercentage ? 'Yes' : 'No';
      // If ratio is more than maxRatio, lower the ratio
      const maxRatioTotalPercentage = 4;
      const lowRatioTotalPercentage = 3;
      if (ratioTotalPercentage > maxRatioTotalPercentage) newRatioTotalPercentage = lowRatioTotalPercentage;

      if (mobileYesNo) {
        let children = [];
        if (mobileYesNo === 'Yes') {
          const yesChildren = Array.from(grouppedYesNo.get('Yes').get(category), (d) => ({
            name: d[nameColumn],
            parent: d['Yes/No'],
            value: d[valueColumn],
            data: d,
          }));
          const yesValueSum = d3.rollups(yesChildren, (v) => d3.sum(v, (d) => d.value));
          const yesGroup = {
            name: 'Yes',
            children: yesChildren,
            value: yesValueSum,
            totalPercentage: yesTotalPercentage,
          };
          children = [yesGroup];
        } else {
          const noChildren = Array.from(grouppedYesNo.get('No').get(category), (d) => ({
            name: d[nameColumn],
            parent: d['Yes/No'],
            value: d[valueColumn],
            data: d,
          }));
          const noValueSum = d3.rollups(noChildren, (v) => d3.sum(v, (d) => d.value));
          const noGroup = {
            name: 'No',
            children: noChildren,
            value: noValueSum,
            totalPercentage: noTotalPercentage,
          };
          children = [noGroup];
        }
        const data = {
          name: 'Root',
          children,
          value: d3.rollups(children, (v) => d3.sum(v, (d) => d.value)),
        };

        return data;
      }

      let yesChildren = Array.from(grouppedYesNo.get('Yes').get(category), (d) => ({
        name: d[nameColumn],
        parent: d['Yes/No'],
        value: d[valueColumn],
        data: d,
      }));
      const yesValueSum = d3.rollups(yesChildren, (v) => d3.sum(v, (d) => d.value));
      // Recalibrate value with total data
      yesChildren = yesChildren.map((d) => {
        const newD = d;
        const realValue = d.value;
        newD.value = (d.value / yesValueSum) * yesTotalPercentage;
        if (minTotalPercentage === 'Yes' && newRatioTotalPercentage > 1) {
          newD.value = (newD.value * ratioTotalPercentage) / newRatioTotalPercentage;
        }
        return {
          ...newD,
          realValue,
        };
      });
      const yesGroup = {
        name: 'Yes',
        children: yesChildren,
        value:
          minTotalPercentage === 'Yes' && newRatioTotalPercentage > 1
            ? (yesTotalPercentage * ratioTotalPercentage) / newRatioTotalPercentage
            : yesTotalPercentage,
        realValue: yesValueSum,
        totalPercentage: yesTotalPercentage,
      };

      let noChildren = Array.from(grouppedYesNo.get('No').get(category), (d) => ({
        name: d[nameColumn],
        parent: d['Yes/No'],
        value: d[valueColumn],
        data: d,
      }));
      const noValueSum = d3.rollups(noChildren, (v) => d3.sum(v, (d) => d.value));
      // Recalibrate value with total data
      noChildren = noChildren.map((d) => {
        const newD = d;
        const realValue = d.value;
        newD.value = (d.value / noValueSum) * noTotalPercentage;
        if (minTotalPercentage === 'No' && newRatioTotalPercentage > 1) {
          newD.value = (newD.value * ratioTotalPercentage) / newRatioTotalPercentage;
        }
        return {
          ...newD,
          realValue,
        };
      });
      const noGroup = {
        name: 'No',
        children: noChildren,
        value:
          minTotalPercentage === 'No' && newRatioTotalPercentage > 1
            ? (noTotalPercentage * ratioTotalPercentage) / newRatioTotalPercentage
            : noTotalPercentage,
        realValue: noValueSum,
        totalPercentage: noTotalPercentage,
      };

      const children = [yesGroup, noGroup];

      const data = {
        name: 'Root',
        children,
        value: yesTotalPercentage + noTotalPercentage,
      };

      return data;
    },
    formatStrToNumCsv(d, colsToConvert) {
      colsToConvert.forEach((col) => {
        d[col] = +d[col];
      });
      return d;
    },
    makeHierarchy(config) {
      const defaultConfig = {
        childrenAccessorFn: ([, value]) => value.size && Array.from(value),
        sumFn: ([, value]) => value,
        sortFn: (a, b) => b.value - a.value,
      };
      const { data, reduceFn, groupByFns, childrenAccessorFn, sumFn, sortFn } = { ...defaultConfig, ...config };
      const rollupData = d3.rollup(data, reduceFn, ...groupByFns);
      const hierarchyData = d3.hierarchy([null, rollupData], childrenAccessorFn).sum(sumFn).sort(sortFn);
      return hierarchyData;
    },
  },
  async mounted() {
    const rawData = await d3.csv(this.dataSrc, (d) =>
      this.formatStrToNumCsv(d, ['Count', 'Percentage', 'Total Responses'])
    );
    const rawDataTotal = await d3.csv(this.dataSrcTotal, (d) =>
      this.formatStrToNumCsv(d, ['Count', 'Percentage', 'Total Responses'])
    );
    delete rawData.columns;
    delete rawDataTotal.columns;
    this.rawData = rawData;
    this.rawDataTotal = rawDataTotal;
    if (!this.$breakpoint.mobile) {
      const grouppedRawData = d3.group(rawData, (d) => d.Parent);

      const rawDataCountry = grouppedRawData.get('Country');
      this.rawDataCountry = rawDataCountry;

      const rawDataGeneration = grouppedRawData.get('Generation');
      this.rawDataGeneration = rawDataGeneration;

      const rawDataGender = grouppedRawData.get('Gender');
      this.rawDataGender = rawDataGender;

      this.categoryParents = this.objectsArrayUnique(rawData, 'Parent');
      // By default categories are by Country
      this.categories = this.objectsArrayUnique(rawDataCountry, 'Category');
      this.data = this.formatData(rawDataCountry, 'Type', 'Percentage', this.category);
    } else {
      const rawDataMYes = rawData.filter((d) => {
        if (d['Yes/No'] === 'Yes') return true;
        return false;
      });
      const rawDataMNo = rawData.filter((d) => {
        if (d['Yes/No'] === 'No') return true;
        return false;
      });
      this.rawDataMYes = rawDataMYes;
      this.rawDataMNo = rawDataMNo;

      const grouppedRawDataMYes = d3.group(rawDataMYes, (d) => d.Parent);
      const grouppedRawDataMNo = d3.group(rawDataMNo, (d) => d.Parent);

      const rawDataCountryMYes = grouppedRawDataMYes.get('Country');
      this.rawDataCountryMYes = rawDataCountryMYes;
      const rawDataCountryMNo = grouppedRawDataMNo.get('Country');
      this.rawDataCountryMNo = rawDataCountryMNo;

      const rawDataGenerationMYes = grouppedRawDataMYes.get('Generation');
      this.rawDataGenerationMYes = rawDataGenerationMYes;
      const rawDataGenerationMNo = grouppedRawDataMNo.get('Generation');
      this.rawDataGenerationMNo = rawDataGenerationMNo;

      const rawDataGenderMYes = grouppedRawDataMYes.get('Gender');
      this.rawDataGenderMYes = rawDataGenderMYes;
      const rawDataGenderMNo = grouppedRawDataMNo.get('Gender');
      this.rawDataGenderMNo = rawDataGenderMNo;

      this.categoryParents = this.objectsArrayUnique(rawDataMYes, 'Parent');
      // By default categories are by Country
      this.categories = this.objectsArrayUnique(rawDataCountryMYes, 'Category');
      this.dataMYes = this.formatData(rawDataCountryMYes, 'Type', 'Percentage', this.category, 'Yes');
      this.dataMNo = this.formatData(rawDataCountryMNo, 'Type', 'Percentage', this.category, 'No');
      this.ratioMYes = Math.min((this.dataMYes.value / this.dataMNo.value) * 1.3, 1);
      this.ratioMNo = Math.min((this.dataMNo.value / this.dataMYes.value) * 1.3, 1);
    }
  },
};
</script>

<style lang="scss" scoped>
.content-packed-circles-investment {
  // padding: 60px 0;
  background-color: #ffffff;

  &__header {
    margin-bottom: 30px;

    .impact-header__controls {
      margin-bottom: 20px;
    }

    .impact-header__commentary {
      background-color: #f0f0f0;
      border-radius: 5px;
      padding: 10px;

      p {
        margin-bottom: 0;
      }
    }
  }

  @media #{map-get($media-breakpoints, 'mobile')} {
    padding: 30px 15px;
  }
}
</style>
